/**
 * Frits — pure logica achter de split-inbox (tabs per categorie, lijst + focus).
 * Geen React en geen netwerk: alles hier is los te testen (fritsTriage.test.js).
 */

// Volgorde van de split-tabs. Categorieën komen uit de triage van de engine
// (beantwoorden/offerte/planning/taak/fyi/nieuwsbrief); nieuwsbrief valt onder FYI.
export const MAIL_TABS = [
  { key: 'beantwoorden', label: 'Beantwoorden' },
  { key: 'offerte', label: 'Offertes' },
  { key: 'planning', label: 'Planning' },
  { key: 'taak', label: 'Taken' },
  { key: 'fyi', label: 'FYI' },
];
export const REMINDER_TAB = { key: 'reminders', label: 'Reminders' };

const CATEGORIE_NAAR_TAB = {
  beantwoorden: 'beantwoorden', offerte: 'offerte', planning: 'planning',
  taak: 'taak', fyi: 'fyi', nieuwsbrief: 'fyi',
};

/** Tab waar een mail in hoort. Onbekende categorie: wacht hij op antwoord → Beantwoorden, anders FYI. */
export function tabVan(item) {
  const tab = CATEGORIE_NAAR_TAB[String(item?.category || '').toLowerCase()];
  if (tab) return tab;
  return item?.needs_reply ? 'beantwoorden' : 'fyi';
}

/** Aantallen per tab, incl. de reminders. */
export function telTabs(items, aantalReminders = 0) {
  const tel = Object.fromEntries(MAIL_TABS.map((t) => [t.key, 0]));
  for (const it of items || []) tel[tabVan(it)] += 1;
  tel[REMINDER_TAB.key] = aantalReminders || 0;
  return tel;
}

/**
 * Welke tabs tonen we? Lege tabs verdwijnen (rust), behalve de actieve tab —
 * anders springt de balk onder je cursor weg.
 */
export function zichtbareTabs(tel, actief) {
  return [...MAIL_TABS, REMINDER_TAB].filter((t) => (tel[t.key] || 0) > 0 || t.key === actief);
}

/** Eerste mail-tab met inhoud; `na` = begin te zoeken ná deze tab (doorwerken). */
export function eersteTabMetMail(tel, na = null) {
  const keys = MAIL_TABS.map((t) => t.key);
  const start = na ? keys.indexOf(na) + 1 : 0;
  const volgorde = [...keys.slice(start), ...keys.slice(0, start)];
  return volgorde.find((k) => (tel[k] || 0) > 0) || null;
}

/** Mails van één tab, in de volgorde van de engine (hoog eerst, dan nieuwste). */
export function mailsVanTab(items, tab) {
  return (items || []).filter((it) => tabVan(it) === tab);
}

/** Na afhandelen: de volgende mail in de lijst (of de vorige als het de laatste was). */
export function volgendeId(lijst, weg) {
  const i = (lijst || []).findIndex((x) => x.id === weg);
  if (i < 0) return lijst?.[0]?.id ?? null;
  return lijst[i + 1]?.id ?? lijst[i - 1]?.id ?? null;
}

/** j/k/pijlen: stap door de lijst, begrensd. */
export function stapId(lijst, huidig, stap) {
  if (!lijst?.length) return null;
  const i = Math.max(0, lijst.findIndex((x) => x.id === huidig));
  const j = Math.min(lijst.length - 1, Math.max(0, i + stap));
  return lijst[j].id;
}

/** Eén regel grijze preview uit de samenvatting. */
export function previewTekst(tekst, max = 140) {
  const t = String(tekst || '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

const pad = (n) => String(n).padStart(2, '0');
const zelfdeDag = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Korte tijd voor de lijst: "09:12" vandaag, "gisteren", anders "24 sep". */
export function kortTijdstip(iso, nu = new Date()) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (zelfdeDag(d, nu)) return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const gisteren = new Date(nu); gisteren.setDate(nu.getDate() - 1);
  if (zelfdeDag(d, gisteren)) return 'gisteren';
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }).replace('.', '');
}

/**
 * "Laatste ronde": het jongste aanmaakmoment van een item. De engine geeft geen
 * losse run-tijd terug; created_at van het nieuwste item is de eerlijke benadering.
 */
export function laatsteRonde(items, nu = new Date()) {
  const tijden = (items || []).map((i) => new Date(i.created_at || 0).getTime()).filter((t) => t > 0);
  if (!tijden.length) return null;
  const d = new Date(Math.max(...tijden));
  const tijd = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (zelfdeDag(d, nu)) return tijd;
  const gisteren = new Date(nu); gisteren.setDate(nu.getDate() - 1);
  if (zelfdeDag(d, gisteren)) return `gisteren ${tijd}`;
  return `${d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} ${tijd}`;
}

/**
 * "Check nu" duurt soms langer dan de proxy wacht (prod: Node-timeout 180 s;
 * Azure-ingress: 240 s). De ronde loopt in de engine gewoon door, dus zo'n fout
 * is geen echte fout: stil terugvallen op pollen. Ook "run al bezig" hoort hierbij.
 */
export function isZachteRunFout(status, bericht) {
  if ([502, 503, 504].includes(Number(status))) return true;
  return /timeout|timed out|exceeded|run al bezig|failed to fetch|networkerror|load failed/i.test(String(bericht || ''));
}

/** Demo-modus (?demo=1): alleen in een dev-build op localhost — nooit op test/prod. */
export function isDemoModus(loc = (typeof window !== 'undefined' ? window.location : null),
  env = process.env.NODE_ENV) {
  if (!loc || env === 'production') return false;
  if (!['localhost', '127.0.0.1'].includes(loc.hostname)) return false;
  return new URLSearchParams(loc.search || '').get('demo') === '1';
}

/** Eerste naam of het deel vóór de @ — voor "Concept aan …". */
export function korteNaam(naam, email) {
  const n = String(naam || '').trim();
  if (n) return n.split(/\s+/)[0];
  return String(email || '').split('@')[0];
}
